/*
 * Variant Filtering with Infinite Scroll for Shopify Dawn Theme
 * Uses URL-based filtering + infinite scroll loading
 * Keeps facet labels stable even after theme DOM mutations
 *
 * Fixes in this version:
 *  - loadNextPage now always advances currentPage (not only when items > 0)
 *    so a zero-result page can't trigger an infinite loop in loadAllPages.
 *  - loadAllPages has a hard safety break (max iterations) just in case.
 *  - Small log added when a page returns 0 items, useful for diagnosing
 *    pagination mismatches (e.g. theme/server returning empty page).
 */

document.addEventListener('DOMContentLoaded', function () {
  // ---- globals (avoid TDZ) ----
  window.__facetObserverDesktop = null;
  window.__facetObserverMobile = null;

  const filterForm = document.getElementById('FacetFiltersForm');
  const mobileFilterForm = document.getElementById('FacetFiltersFormMobile');
  const productGrid = document.getElementById('product-grid');
  const loadingIndicator = document.getElementById('infinite-loading');
  const endIndicator = document.getElementById('infinite-end');

  // i18n injected by Liquid (optional)
  const i18nEl = document.getElementById('infinite-scroll-i18n');
  let I18N = {
    loading_products: 'Meer producten laden...',
    loaded_products: 'Alle producten zijn geladen',
    product_singular: 'product',
    product_plural: 'producten',
  };
  if (i18nEl) {
    try {
      I18N = Object.assign(I18N, JSON.parse(i18nEl.textContent || '{}'));
    } catch (_) {}
  }

  // early exit if not on a collection/search page
  if (!productGrid || (!filterForm && !mobileFilterForm)) return;

  // paging state
  let currentPage = parseInt(productGrid.dataset.currentPage) || 1;
  let totalPages = parseInt(productGrid.dataset.totalPages) || 1;
  let isLoading = false;
  let isLoadingAllPages = false;
  let isInitializing = true;

  // already-rendered variant items
  let variantItems = productGrid.querySelectorAll('.product-variant-item');

  // kick things off
  initialize();

  // =========================================================
  // init
  // =========================================================
  function initialize() {
    setLoadingState(true);
    updatePriceRangeText();

    // first pass: fix labels now (prevents disappear/truncate)
    normalizeFacetLabels();
    ensureStableFacetText();

    // watch for Dawn/Shopify script mutations
    startFacetObservers();

    if (filterForm) setupDesktopForm();
    if (mobileFilterForm) setupMobileForm();
    preventFilterAutoClose();

    // "clear all" buttons
    document.addEventListener('click', (e) => {
      if (
        e.target.matches(
          '.active-facets__button-remove, .active-filters__button-remove, [data-clear-filters]'
        )
      ) {
        e.preventDefault();
        setLoadingState(true);
        window.location.href = window.location.pathname;
      }
    });

    // wait for Dawn facets to finish first render
    let wait = setInterval(() => {
      const items = productGrid.querySelectorAll(
        '.product-variant-item, .grid__item:not(.product-variant-item)'
      );
      const overlay = document.querySelector('.loading-overlay');
      const stillLoading =
        overlay && window.getComputedStyle(overlay).display !== 'none';

      if (!stillLoading && items.length > 0) {
        clearInterval(wait);

        variantItems = productGrid.querySelectorAll('.product-variant-item');
        initInfiniteScroll();
        applyClientSideFiltering();

        // second pass, just in case theme changed labels
        normalizeFacetLabels();
        ensureStableFacetText();

        if (filterForm) addPriceApplyButton();

        isInitializing = false;
        if (!isLoadingAllPages && currentPage >= totalPages) updateProductCount();
      }
    }, 100);

    // failsafe
    setTimeout(() => {
      if (isInitializing) {
        clearInterval(wait);
        variantItems = productGrid.querySelectorAll('.product-variant-item');
        initInfiniteScroll();
        applyClientSideFiltering();
        normalizeFacetLabels();
        ensureStableFacetText();
        if (filterForm) addPriceApplyButton();
        isInitializing = false;
        if (!isLoadingAllPages && currentPage >= totalPages) updateProductCount();
      }
    }, 5000);
  }

  function setLoadingState(loading) {
    const desktop = document.getElementById('ProductCountDesktop');
    const mobile = document.getElementById('ProductCount');
    if (loading) {
      if (desktop) desktop.textContent = I18N.loading_products;
      if (mobile) mobile.textContent = I18N.loading_products;
    }
  }

  // =========================================================
  // desktop + mobile forms
  // =========================================================
  function setupDesktopForm() {
    filterForm.addEventListener('submit', (e) => e.preventDefault());

    filterForm.addEventListener('change', (e) => {
      if (e.target.type === 'checkbox' || e.target.type === 'radio') {
        if (e.target.name === 'filter.v.availability') {
          filterForm
            .querySelectorAll('input[name="filter.v.availability"]')
            .forEach((cb) => {
              if (cb !== e.target) cb.checked = false;
            });
        }
        updateURL();
      }
    });

    // sort dropdowns anywhere on page
    document.addEventListener('change', (e) => {
      if (
        e.target.name === 'sort_by' &&
        (e.target.id === 'SortBy' ||
          e.target.classList.contains('facet-filters__sort'))
      ) {
        setLoadingState(true);
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', e.target.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      }
    });

    // price range "apply" flow
    let priceTimeout;
    filterForm.addEventListener('input', (e) => {
      if (e.target.type === 'number' || e.target.type === 'range') {
        clearTimeout(priceTimeout);
        showPriceApplyButton();
      }
    });
  }

  function setupMobileForm() {
    // availability exclusive
    mobileFilterForm.addEventListener('change', (e) => {
      if (
        e.target.type === 'checkbox' &&
        e.target.name === 'filter.v.availability'
      ) {
        const all = document.querySelectorAll(
          'input[name="filter.v.availability"]'
        );
        if (e.target.checked) {
          all.forEach((cb) => {
            if (cb !== e.target) cb.checked = false;
          });
        }
      }
    });

    // track sort change; applied on "Toepassen"
    setTimeout(() => {
      const sel = document.getElementById('SortBy-mobile');
      if (sel) {
        sel.addEventListener('change', (e) =>
          console.log('Mobile sort changed:', e.target.value)
        );
      }
    }, 1000);

    mobileFilterForm.addEventListener('submit', (e) => e.preventDefault());

    mobileFilterForm.addEventListener('click', (e) => {
      if (
        e.target.type === 'submit' ||
        e.target.classList.contains('mobile-facets__apply') ||
        e.target.textContent?.toLowerCase().includes('toepassen') ||
        e.target.closest('[type="submit"]')
      ) {
        e.preventDefault();
        e.stopPropagation();
        updateURLFromMobile();
      }
    });

    // when drawer closes, apply if there are changes
    document.addEventListener('click', (e) => {
      if (
        e.target.matches('.mobile-facets__close, .modal__close-button') ||
        e.target.closest('.mobile-facets__close, .modal__close-button')
      ) {
        setTimeout(() => {
          const mf = document.getElementById('FacetFiltersFormMobile');
          if (mf && hasMobileFormChanges()) updateURLFromMobile();
        }, 100);
      }
    });
  }

  function updateURL() {
    setLoadingState(true);
    const url = new URL(window.location.href);
    const formData = new FormData(filterForm);

    // clear existing filter params
    const toDelete = [];
    url.searchParams.forEach((_, key) => {
      if (key.startsWith('filter.')) toDelete.push(key);
    });
    toDelete.forEach((k) => url.searchParams.delete(k));

    // add active filters
    for (const [key, value] of formData.entries()) {
      if (value && key.startsWith('filter.')) url.searchParams.append(key, value);
    }

    url.searchParams.delete('page');
    window.location.href = url.toString();
  }

  function updateURLFromMobile() {
    setLoadingState(true);
    const url = new URL(window.location.href);
    const formData = new FormData(mobileFilterForm);

    const currentSortBy = url.searchParams.get('sort_by');

    // clear filter params
    const toDelete = [];
    url.searchParams.forEach((_, key) => {
      if (key.startsWith('filter.')) toDelete.push(key);
    });
    toDelete.forEach((k) => url.searchParams.delete(k));

    // add active filters
    for (const [key, value] of formData.entries()) {
      if (value && key.startsWith('filter.')) url.searchParams.append(key, value);
    }

    // sorting
    const mobileSort = document.getElementById('SortBy-mobile');
    if (mobileSort && mobileSort.value) {
      url.searchParams.set('sort_by', mobileSort.value);
    } else if (currentSortBy) {
      url.searchParams.set('sort_by', currentSortBy);
    }

    url.searchParams.delete('page');
    window.location.href = url.toString();
  }

  function hasMobileFormChanges() {
    if (!mobileFilterForm) return false;
    const formData = new FormData(mobileFilterForm);
    for (const [key, value] of formData.entries()) {
      if (value && key.startsWith('filter.')) return true;
    }
    return false;
  }

  // =========================================================
  // infinite scroll
  // =========================================================
  function initInfiniteScroll() {
    if (currentPage >= totalPages) {
      if (endIndicator) endIndicator.style.display = 'none'; // was block
      return;
    }
    loadAllPages();
  }

  async function loadAllPages() {
    isLoadingAllPages = true;

    if (loadingIndicator) {
      loadingIndicator.style.display = 'block';
      const p = loadingIndicator.querySelector('p');
      if (p) p.textContent = I18N.loading_products;
    }

    // FIX: hard safety break — refuses to loop more than (totalPages + 5) times
    // in case currentPage somehow fails to advance. Prevents the runaway
    // request loop that was theoretically possible in the previous version.
    let safety = 0;
    const maxIterations = (totalPages || 1) + 5;

    while (currentPage < totalPages && !isLoading && safety < maxIterations) {
      await loadNextPage();
      safety++;
      await new Promise((r) => setTimeout(r, 100));
    }

    if (safety >= maxIterations) {
      console.warn(
        '[variant-filtering] loadAllPages reached safety limit',
        { currentPage, totalPages, safety }
      );
    }

    isLoadingAllPages = false;
    updateProductCount();
  }

  async function loadNextPage() {
    if (isLoading || currentPage >= totalPages) return;
    isLoading = true;

    const nextPage = currentPage + 1;
    if (loadingIndicator) loadingIndicator.style.display = 'block';

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('page', nextPage);

      const res = await fetch(url.toString());
      const html = await res.text();

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGrid = doc.getElementById('product-grid');
      const newItems = newGrid ? newGrid.querySelectorAll('.grid__item') : [];

      if (newItems.length > 0) {
        newItems.forEach((item) => productGrid.appendChild(item.cloneNode(true)));

        variantItems = productGrid.querySelectorAll('.product-variant-item');

        applyClientSideFiltering();
        normalizeFacetLabels();
        ensureStableFacetText();

        // keep observing after new content
        startFacetObservers();
      } else {
        // Empty page – nothing rendered, but we still need to advance so the
        // while-loop in loadAllPages doesn't fetch this URL forever.
        console.warn(
          '[variant-filtering] page returned 0 items',
          { page: nextPage, url: url.toString() }
        );
      }

      // FIX: advance currentPage UNCONDITIONALLY (was only inside the
      // newItems.length > 0 branch). Without this, an empty page causes
      // loadAllPages to retry the same page indefinitely.
      currentPage = nextPage;
      productGrid.dataset.currentPage = currentPage;

      if (currentPage >= totalPages && endIndicator) {
        endIndicator.style.display = 'block';
        const p = endIndicator.querySelector('p');
        if (p) p.textContent = I18N.loaded_products;
      }
    } catch (err) {
      console.error('Error loading next page:', err);
      // On error: also advance to prevent retry-loop on a broken page.
      currentPage = nextPage;
      productGrid.dataset.currentPage = currentPage;
    } finally {
      isLoading = false;
      if (loadingIndicator) loadingIndicator.style.display = 'none';
    }
  }

  // =========================================================
  // facet labels: normalize + make stable copies
  // =========================================================
  function normalizeFacetLabels() {
    document.querySelectorAll('.facets__item').forEach((item) => {
      const label = item.querySelector('label');
      if (!label) return;

      let textSpan = label.querySelector('.facet-checkbox__text');
      let countSpan = label.querySelector('.facet-checkbox__count');

      const raw = (textSpan ? textSpan.textContent : label.textContent).trim();
      const m = raw.match(/\s*\((\d+)\)\s*$/);
      const baseText = raw.replace(/\s*\(\d+\)\s*$/, '').trim();
      const countText = m ? `(${m[1]})` : '';

      if (!textSpan) {
        textSpan = document.createElement('span');
        textSpan.className = 'facet-checkbox__text';
        label.insertBefore(textSpan, label.firstChild);
      }
      textSpan.textContent = baseText;

      if (!countSpan) {
        countSpan = document.createElement('span');
        countSpan.className = 'facet-checkbox__count';
        label.appendChild(countSpan);
      }
      countSpan.textContent = countText;
    });
  }

  // Build "stable" text spans but REMOVE counts like (13)
  function ensureStableFacetText(root = document) {
    const labels = root.querySelectorAll('.facets__item label, .mobile-facets__label');

    labels.forEach((label) => {
      // Remove old stable nodes if present (prevents duplicates)
      label.querySelectorAll('.facet-text-stable, .facet-count-stable').forEach(el => el.remove());

      const originalText = label.querySelector('.facet-checkbox__text');
      const originalCount = label.querySelector('.facet-checkbox__count');

      // Clean label text — strip out (numbers)
      let baseText = (originalText ? originalText.textContent : label.textContent || '').trim();
      baseText = baseText.replace(/\(\d+\)/g, '').trim();

      // Create single stable text span (no count)
      const textSpan = document.createElement('span');
      textSpan.className = 'facet-text-stable';
      textSpan.textContent = baseText;

      // Insert near the icon
      const firstIcon = label.querySelector(':scope > svg, :scope > .svg-wrapper');
      if (firstIcon && firstIcon.nextSibling) {
        label.insertBefore(textSpan, firstIcon.nextSibling);
      } else {
        label.appendChild(textSpan);
      }

      // Flex layout for wrapping long text
      label.style.display = 'flex';
      label.style.alignItems = 'flex-start';
      label.style.gap = '.4rem';
      label.style.minWidth = '0';
      textSpan.style.minWidth = '0';
      textSpan.style.whiteSpace = 'normal';
      textSpan.style.overflowWrap = 'anywhere';
      textSpan.style.wordBreak = 'break-word';

      // Hide original text and count so they don’t render twice
      if (originalText)  { originalText.setAttribute('aria-hidden', 'true'); originalText.style.display = 'none'; }
      if (originalCount) { originalCount.setAttribute('aria-hidden', 'true'); originalCount.style.display = 'none'; }
    });
  }

  function startFacetObservers() {
    const debounce = (fn, wait = 60) => {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
      };
    };

    const reapply = debounce(() => {
      normalizeFacetLabels();
      ensureStableFacetText();
    }, 60);

    const observe = (el, ref) => {
      if (!el) return;

      if (ref === 'desktop' && window.__facetObserverDesktop)
        window.__facetObserverDesktop.disconnect();
      if (ref === 'mobile' && window.__facetObserverMobile)
        window.__facetObserverMobile.disconnect();

      const obs = new MutationObserver((muts) => {
        if (
          muts.some(
            (m) =>
              m.type === 'childList' ||
              (m.type === 'attributes' && m.attributeName === 'class')
          )
        ) {
          reapply();
        }
      });

      obs.observe(el, { subtree: true, childList: true, attributes: true });

      if (ref === 'desktop') window.__facetObserverDesktop = obs;
      if (ref === 'mobile') window.__facetObserverMobile = obs;
    };

    observe(
      document.getElementById('FacetFiltersForm') ||
        document.querySelector('.facets'),
      'desktop'
    );
    observe(
      document.getElementById('FacetFiltersFormMobile') ||
        document.querySelector('.mobile-facets'),
      'mobile'
    );

    // first run
    normalizeFacetLabels();
    ensureStableFacetText();
  }

  // =========================================================
  // client-side filtering helpers
  // =========================================================
  function applyClientSideFiltering() {
    const urlParams = new URLSearchParams(window.location.search);
    const hasVariantFilters = Array.from(urlParams.keys()).some(
      (k) => k.includes('filter.v.m.custom.') || k.includes('filter.v.availability')
    );
    if (!hasVariantFilters) return;

    const variantFilters = {};
    const availabilityFilter = [];

    urlParams.forEach((value, key) => {
      if (key.includes('filter.v.m.custom.')) {
        const name = key.replace('filter.v.m.custom.', '');
        if (!variantFilters[name]) variantFilters[name] = [];
        variantFilters[name].push(String(value).toLowerCase());
      } else if (key === 'filter.v.availability') {
        availabilityFilter.push(value);
      }
    });

    let visibleCount = 0;
    variantItems.forEach((item) => {
      let show = true;

      // availability
      if (availabilityFilter.length > 0) {
        const isAvailable = item.dataset.available === 'true';
        const showInStock = availabilityFilter.includes('1');
        const showOutOfStock = availabilityFilter.includes('0');
        if (showInStock && !isAvailable) show = false;
        if (showOutOfStock && isAvailable) show = false;
      }

      // variant metafields
      Object.entries(variantFilters).forEach(([name, values]) => {
        const datasetKey = `variant${name.charAt(0).toUpperCase()}${name.slice(1)}`;
        const val = (item.dataset[datasetKey] || '').toLowerCase();
        if (!values.some((v) => val.includes(v))) show = false;
      });

      item.style.display = show ? '' : 'none';
      if (show) visibleCount++;
    });

    if (!isLoadingAllPages && currentPage >= totalPages) {
      updateProductCount(visibleCount);
    }
    showClientSideFilterNotice(hasVariantFilters, visibleCount);
  }

  function showClientSideFilterNotice(show, visibleCount) {
    const id = 'client-filter-notice';
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    if (!show || visibleCount > 5) return;

    const notice = document.createElement('div');
    notice.id = id;
    notice.style.cssText =
      'background:#F9F6F5;padding:20px;margin:20px 0;text-align:center;border-radius:8px;border:1px solid #AC5B40;';
    notice.textContent = ''; // (intentionally empty – customize if you want)

    const grid = document.querySelector('#product-grid');
    if (grid && grid.parentNode) grid.parentNode.insertBefore(notice, grid);
  }

  function preventFilterAutoClose() {
    const userOpened = new Set();
    const detailsEls = document.querySelectorAll(
      '.facets__disclosure, .facets__disclosure-vertical, .mobile-facets__details'
    );

    detailsEls.forEach((details) => {
      details.addEventListener('toggle', () => {
        if (details.open) userOpened.add(details.id);
        else userOpened.delete(details.id);
      });

      details.addEventListener('change', (e) => {
        if (
          (e.target.type === 'checkbox' || e.target.type === 'radio') &&
          userOpened.has(details.id) &&
          details.open
        ) {
          setTimeout(() => {
            if (userOpened.has(details.id)) details.open = true;
          }, 50);
        }
      });
    });
  }

  // =========================================================
  // misc UI helpers
  // =========================================================
  function addPriceApplyButton() {
    const priceRange = document.querySelector('price-range');
    if (!priceRange) return;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'button button--secondary';
    btn.textContent = 'Toepassen';
    btn.style.cssText = 'margin-top:10px;display:none;';
    btn.id = 'price-apply-button';

    btn.addEventListener('click', () => {
      updateURL();
      btn.style.display = 'none';
    });

    priceRange.appendChild(btn);
  }

  function showPriceApplyButton() {
    const btn = document.getElementById('price-apply-button');
    if (btn) btn.style.display = 'block';
  }

  function updatePriceRangeText() {
    let min = Infinity;
    let max = 0;

    variantItems.forEach((item) => {
      if (item.style.display !== 'none') {
        const p = parseInt(item.dataset.price) || 0;
        if (p < min) min = p;
        if (p > max) max = p;
      }
    });

    document.querySelectorAll('.price-range__display').forEach((el) => {
      if (el.textContent.includes('hoogste prijs')) {
        el.textContent = `De hoogste prijs is €${(max / 100)
          .toFixed(2)
          .replace('.', ',')}`;
      }
    });
  }

  function updateProductCount(count = null) {
    if (isInitializing) return;

    const desktop = document.getElementById('ProductCountDesktop');
    const mobile = document.getElementById('ProductCount');

    if (isLoadingAllPages || currentPage < totalPages) {
      if (desktop) desktop.textContent = I18N.loading_products;
      if (mobile) mobile.textContent = I18N.loading_products;
      return;
    }

    if (count !== null) {
      const text =
        count === 1
          ? `1 ${I18N.product_singular || 'product'}`
          : `${count} ${I18N.product_plural || 'producten'}`;
      if (desktop) desktop.textContent = text;
      if (mobile) mobile.textContent = text;
    } else {
      const all = productGrid.querySelectorAll(
        '.product-variant-item, .grid__item:not(.product-variant-item)'
      );
      const visible = Array.from(all).filter(
        (n) => !n.style.display || n.style.display !== 'none'
      );
      const n = visible.length;
      const text =
        n === 1
          ? `1 ${I18N.product_singular || 'product'}`
          : `${n} ${I18N.product_plural || 'producten'}`;
      if (desktop) desktop.textContent = text;
      if (mobile) mobile.textContent = text;
    }
  }

  // =========================================================
  // debug helper
  // =========================================================
  window.debugFilters = function () {
    console.log('=== FILTER DEBUG ===');
    console.log('URL:', window.location.href);

    const params = new URLSearchParams(window.location.search);
    params.forEach((v, k) => {
      if (k.startsWith('filter.')) console.log('  ', k, '=', v);
    });

    console.log(
      'Visible variants:',
      document.querySelectorAll(
        '.product-variant-item:not([style*="display: none"])'
      ).length
    );
    console.log(
      'Total variants:',
      document.querySelectorAll('.product-variant-item').length
    );
    console.log('Page', currentPage, '/', totalPages);
    console.log('isLoading:', isLoading, 'isLoadingAllPages:', isLoadingAllPages);
    console.log('Observers:', {
      desktop: !!window.__facetObserverDesktop,
      mobile: !!window.__facetObserverMobile,
    });
  };
});